// hooks/useDomainFilters.js - Production domain filters hook
import { useCallback, useEffect, useState } from "react";
import apiService from "../services/apiService";

export const useDomainFilters = () => {
	const [filterValues, setFilterValues] = useState(null);
	const [safetyFilters, setSafetyFilters] = useState(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [authAttempted, setAuthAttempted] = useState(false);
	const [googleReady, setGoogleReady] = useState(false);
	const [filtersLoaded, setFiltersLoaded] = useState(false);

	// Check if Google Sign-In is ready
	const checkGoogleReady = useCallback(() => {
		if (window.google?.accounts?.id) {
			setGoogleReady(true);
			return true;
		}
		return false;
	}, []);

	// Wait for Google to be ready
	useEffect(() => {
		if (checkGoogleReady()) {
			return;
		}

		const checkInterval = setInterval(() => {
			if (checkGoogleReady()) {
				clearInterval(checkInterval);
			}
		}, 100);

		const timeout = setTimeout(() => {
			clearInterval(checkInterval);
			setGoogleReady(true);
		}, 10000);

		return () => {
			clearInterval(checkInterval);
			clearTimeout(timeout);
		};
	}, [checkGoogleReady]);

	const mapCategoriesToIAB = useCallback((categories) => {
		if (!categories || !Array.isArray(categories) || categories.length === 0) {
			return [];
		}
		return categories.filter(Boolean);
	}, []);

	const mapFiltersForAPI = useCallback(
		(filters) => {
			if (!filters) return filters;
			const mappedFilters = { ...filters };

			// ✅ Categories mapping
			if (
				filters.categories &&
				Array.isArray(filters.categories) &&
				filters.categories.length > 0
			) {
				mappedFilters.iab_category = mapCategoriesToIAB(filters.categories);
			}

			// ✅ Period mapping
			if (filters.period !== undefined) {
				mappedFilters.period = filters.period;
			}

			// ✅ Targeting Filters - MISSING!
			if (filters.mediaSubtype) {
				mappedFilters.mediaSubtype = filters.mediaSubtype;
			}

			if (filters.deviceTypes) {
				mappedFilters.deviceTypes = filters.deviceTypes;
			}

			if (filters.videoPlacement) {
				mappedFilters.videoPlacement = filters.videoPlacement;
			}

			if (filters.selectedCountries) {
				mappedFilters.selectedCountries = filters.selectedCountries;
			}

			// ✅ Content Specifications - MISSING!
			if (filters.contentStyle) {
				mappedFilters.contentStyle = filters.contentStyle;
			}

			if (filters.safetyTier) {
				mappedFilters.safetyTier = filters.safetyTier;
			}

			// ✅ Performance Metrics
			if (filters.minViewability !== undefined) {
				mappedFilters.minViewability = parseFloat(filters.minViewability) || 0;
			}

			if (filters.minReach !== undefined) {
				mappedFilters.minReach = parseFloat(filters.minReach) || 0;
			}

			if (filters.customFilters && Array.isArray(filters.customFilters)) {
				mappedFilters.customFilters = filters.customFilters.map((filter) => ({
					...filter,
					threshold: parseFloat(filter.threshold) || 0,
				}));
			}

			if (filters.isDirect !== undefined) {
				mappedFilters.isDirect = filters.isDirect;
			}

			if (filters.publishers) {
				mappedFilters.publishers = filters.publishers;
			}

			if (filters.limit !== undefined) {
				mappedFilters.limit = parseInt(filters.limit) || 5000;
			}

			if (filters.uploadedDomains) {
				mappedFilters.uploadedDomains = filters.uploadedDomains;
			}

			return mappedFilters;
		},
		[mapCategoriesToIAB],
	);

	const mapSafetyFilters = useCallback((responseData) => {
		if (!responseData) {
			return null;
		}

		const domainFilters = responseData.domainFilterValues;

		if (!domainFilters) {
			return null;
		}

		const { content_style, safetyTier } = domainFilters;

		const contentStyles = (content_style || [])
			.filter((item) => item !== null && item !== undefined && item !== "")
			.filter((item, index, arr) => arr.indexOf(item) === index)
			.sort();

		const safetyTiers = (safetyTier || [])
			.filter((item) => item !== null && item !== undefined && item !== "")
			.filter((item, index, arr) => arr.indexOf(item) === index)
			.sort();

		const mappedSafetyFilters = {
			contentStyle: contentStyles,
			safetyTier: safetyTiers,

			// Helper methods
			getMainTiers: () => safetyTiers,
			getAllContentStyles: () => contentStyles,
			hasMainTiers: () => safetyTiers.length > 0,
			hasTier: (tierName) => safetyTiers.includes(tierName),
			hasContentStyle: (styleName) => contentStyles.includes(styleName),

			// Safety checks
			isFamilyFriendly: () => safetyTiers.includes("Family"),
			isTeenAppropriate: () =>
				safetyTiers.includes("Teen") || safetyTiers.includes("Family"),
			isMatureContent: () => safetyTiers.includes("Mature"),
			isRestricted: () => safetyTiers.includes("Restricted"),
			isUniversal: () => safetyTiers.includes("Universal"),
			isStandard: () => safetyTiers.includes("Standard"),

			// Content style helpers
			isPromotional: () => contentStyles.includes("Promotional"),
			isInformative: () => contentStyles.includes("Informative"),
			isInstructional: () => contentStyles.includes("Instructional Content"),
			isReference: () => contentStyles.includes("Reference"),
			isAggregated: () => contentStyles.includes("Aggregated/Curated"),
		};

		return mappedSafetyFilters;
	}, []);

	const loadFilters = useCallback(
		async (forceReload = false) => {
			if (!apiService.isAuthenticated()) {
				setError("Authentication required");
				setIsAuthenticated(false);
				return;
			}

			if (filtersLoaded && !forceReload) {
				return;
			}

			setIsLoading(true);
			setError(null);

			try {
				const result = await apiService.getDomainFilterValues();

				if (result.success) {
					const { data } = result;

					// Set safety filters
					const mappedSafetyFilters = mapSafetyFilters(data);
					setSafetyFilters(mappedSafetyFilters);

					// Process main filter data
					const domainFilters = data?.domainFilterValues || {};

					const {
						content_style,
						safetyTier,
						categories,
						iab_category,
						...otherFilters
					} = domainFilters;

					const consolidatedContentStyle = (content_style || [])
						.filter(
							(item) => item !== null && item !== undefined && item !== "",
						)
						.filter((item, index, arr) => arr.indexOf(item) === index)
						.sort();

					const consolidatedSafetyTier = (safetyTier || [])
						.filter(
							(item) => item !== null && item !== undefined && item !== "",
						)
						.filter((item, index, arr) => arr.indexOf(item) === index)
						.sort();

					setFilterValues({
						...data,
						domainFilterValues: {
							...otherFilters,
							categories: iab_category || categories || [],
							contentStyle: consolidatedContentStyle,
							safetyTier: consolidatedSafetyTier,
						},
					});

					setFiltersLoaded(true);
				} else {
					if (
						result.error?.includes("401") ||
						result.error?.includes("403") ||
						result.error?.includes("Authentication") ||
						result.error?.includes("Unauthorized")
					) {
						setError("Authentication required");
						setIsAuthenticated(false);
						setAuthAttempted(false);
						setFilterValues(null);
						setSafetyFilters(null);
						setFiltersLoaded(false);
					} else {
						setError(result.error || "Failed to load filters");
					}
				}
			} catch (err) {
				if (
					err.message?.includes("Authentication") ||
					err.message?.includes("401") ||
					err.message?.includes("403")
				) {
					setError("Authentication required");
					setIsAuthenticated(false);
					setAuthAttempted(false);
					setFilterValues(null);
					setSafetyFilters(null);
					setFiltersLoaded(false);
				} else {
					setError(err.message || "Network error loading filters");
				}
			} finally {
				setIsLoading(false);
			}
		},
		[mapSafetyFilters, filtersLoaded],
	);

	const signIn = useCallback(async () => {
		setIsLoading(true);
		setError(null);

		try {
			const result = await apiService.signIn();

			if (result.success) {
				setAuthAttempted(true);
				setIsAuthenticated(true);
				setError(null);

				if (!filtersLoaded) {
					setTimeout(() => {
						loadFilters();
					}, 100);
				}

				return result;
			} else {
				const errorMsg = result.error || "Sign-in failed";
				setError(errorMsg);
				setAuthAttempted(true);
				throw new Error(errorMsg);
			}
		} catch (error) {
			setError(error.message || "Sign-in failed");
			setAuthAttempted(true);
			throw error;
		} finally {
			setIsLoading(false);
		}
	}, [loadFilters, filtersLoaded]);

	// Check initial auth status
	useEffect(() => {
		const checkInitialAuth = () => {
			const authStatus = apiService.isAuthenticated();
			setIsAuthenticated(authStatus);

			if (authStatus && !filtersLoaded) {
				setError(null);
				loadFilters();
			} else if (!authStatus) {
				setAuthAttempted(false);
			}
		};

		checkInitialAuth();

		const unsubscribe = apiService.onTokenChange((event, status) => {
			setIsAuthenticated(status.isAuthenticated);

			if (event === "stored" && !filtersLoaded) {
				setError(null);
				setAuthAttempted(true);

				setTimeout(() => {
					if (status.isAuthenticated) {
						loadFilters();
					}
				}, 50);
			} else if (event === "refreshed") {
				setError(null);
				setAuthAttempted(true);
			} else if (event === "cleared" || event === "expired") {
				setFilterValues(null);
				setSafetyFilters(null);
				setError("Authentication required");
				setAuthAttempted(false);
				setFiltersLoaded(false);
			}
		});

		return unsubscribe;
	}, [loadFilters, filtersLoaded]);

	const clearErrorAndRetry = useCallback(async () => {
		setError(null);
		setIsLoading(true);

		try {
			if (apiService.isAuthenticated()) {
				await loadFilters(true);
			} else {
				await signIn();
			}
		} catch (error) {
			console.error("Retry failed:", error);
		} finally {
			setIsLoading(false);
		}
	}, [loadFilters, signIn]);

	const retry = useCallback(async () => {
		setError(null);

		if (apiService.isAuthenticated()) {
			await loadFilters(true);
		} else {
			try {
				await signIn();
			} catch (err) {
				console.error("Retry sign-in failed:", err);
			}
		}
	}, [loadFilters, signIn]);

	const refresh = useCallback(async () => {
		setIsLoading(true);
		setError(null);

		try {
			await apiService.refreshToken();
		} catch (error) {
			try {
				await signIn();
			} catch (signInError) {
				setError("Authentication failed");
				setIsAuthenticated(false);
				setAuthAttempted(true);
			}
		} finally {
			setIsLoading(false);
		}
	}, [signIn]);

	const refreshFilters = useCallback(async () => {
		if (apiService.isAuthenticated()) {
			await loadFilters(true);
		} else {
			setError("Authentication required to refresh filters");
		}
	}, [loadFilters]);

	const needsAuth = !isAuthenticated && !isLoading;

	const isAuthError =
		error &&
		(error.includes("Authentication") ||
			error.includes("401") ||
			error.includes("403") ||
			error.includes("Unauthorized") ||
			error === "Authentication required");

	// Auto-attempt sign-in when necessary
	useEffect(() => {
		if (
			!isAuthenticated &&
			!authAttempted &&
			!isLoading &&
			googleReady &&
			!filtersLoaded
		) {
			signIn().catch((err) => {
				// Silent error handling for auto sign-in
			});
		}
	}, [
		isAuthenticated,
		authAttempted,
		isLoading,
		googleReady,
		filtersLoaded,
		signIn,
	]);

	return {
		// Data
		filterValues,
		safetyFilters,

		// Loading states
		isLoading,
		error,

		// Authentication states
		isAuthenticated,
		needsAuth,
		isAuthError,
		authAttempted,
		googleReady,
		filtersLoaded,

		// Actions
		signIn,
		retry,
		refresh,
		refreshFilters,
		mapFiltersForAPI,
		mapCategoriesToIAB,
		loadFilters,
		clearErrorAndRetry,

		// Additional utilities
		user: apiService.getUser(),
		authStatus: apiService.isAuthenticated(),

		// Safety filter utilities
		hasSafetyFilters: Boolean(safetyFilters),
		getSafetyTiers: () => safetyFilters?.getMainTiers() || [],
		checkSafetyTier: (tierName) => safetyFilters?.hasTier(tierName) || false,
	};
};
